import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NgxMaskDirective } from 'ngx-mask';

import { NgClass } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { SecondaryLogoComponent } from '@app/components/shared/secondary-logo/secondary-logo.component';
import { AUTHENTICATOR_CONFIG } from '@app/utils/constants/authenticator.constants';
import { AppRoutes } from '@app/utils/enums/routes.enum';
import { REGEX } from '@app/utils/constants/regex.constants';
import { KeyboardKeys } from '@app/utils/enums/keyboard-keys.enum';
import { HttpStatusCode } from '@app/utils/enums/http-status-codes.enum';
import { StatusModalComponent } from '@app/components/shared/status-modal/status-modal.component';
import { MFAService } from '@app/pages/login-page/services/verify-totp.service';
import { AuthService } from '@app/services/auth/auth.service';

@Component({
  selector: 'app-authenticator',
  standalone: true,
  imports: [
    TranslateModule,
    SecondaryLogoComponent,
    ReactiveFormsModule,
    NgxMaskDirective,
    NgClass,
  ],
  templateUrl: './authenticator.component.html',
  styleUrl: './authenticator.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthenticatorComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly dialog = inject(MatDialog);
  private readonly mfaService = inject(MFAService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  protected totpForm!: FormGroup;

  protected loading = signal<boolean>(false);
  protected isTotpInvalid = signal<boolean>(false);

  public ngOnInit(): void {
    this.totpForm = this.fb.group({
      code: this.fb.array(
        Array(AUTHENTICATOR_CONFIG.codeLength)
          .fill(null)
          .map(() => this.fb.control('', [Validators.required])),
      ),
    });
  }

  protected get codeArray(): FormArray {
    return this.totpForm.get('code') as FormArray;
  }

  onDigitInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(REGEX.nonDigitChar, '');

    this.isTotpInvalid.set(false);

    if (value) {
      this.codeArray.at(index).setValue(value);
      const nextInput = input.nextElementSibling as HTMLInputElement | null;
      nextInput?.focus();
      nextInput?.select();
    }
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;

    if (event.key === KeyboardKeys.BACKSPACE && !input.value && index > 0) {
      const prevInput = input.previousElementSibling as HTMLInputElement | null;
      prevInput?.focus();
      this.codeArray.at(index - 1).setValue('');
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') ?? '';
    const digits = pastedData.replace(REGEX.nonDigitChar, '').split('');

    if (digits.length === AUTHENTICATOR_CONFIG.codeLength) {
      digits.forEach((digit, i) => {
        this.codeArray.at(i).setValue(digit);
      });

      const lastInput = (
        event.target as HTMLInputElement
      ).parentElement?.querySelectorAll<HTMLInputElement>('input')[
        AUTHENTICATOR_CONFIG.codeLength - 1
      ];
      lastInput?.focus();
    }
  }

  onSubmit(): void {
    if (this.totpForm.invalid) {
      this.totpForm.markAllAsTouched();
      return;
    }

    const totpCode = this.codeArray.controls.map((c) => c.value).join('');
    this.loading.set(true);
    this.isTotpInvalid.set(false);
    this.totpForm.disable();

    this.mfaService
      .verifyTotp({ totpCode })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
          this.totpForm.enable();
        }),
      )
      .subscribe({
        next: (response) => {
          this.authService.setSession(response.data);
          this.router.navigate([AppRoutes.MAIN]);
        },
        error: (error) => {
          if (error.status === HttpStatusCode.UNAUTHORIZED) {
            this.isTotpInvalid.set(true);
          } else {
            this.dialog.open(StatusModalComponent, {
              data: {
                isSuccess: false,
                title: 'authenticatorPage.errors.errorTitle',
                description: 'authenticatorPage.errors.errorText',
              },
            });
          }
        },
      });
  }
}
